import type { ViewsService } from '../../Views.service';
import type { IConfigProps, ReportConfigEntryType } from './interfaces';
import type { IReportValueAccessor } from './interfaces';
import { type IReportDataGatherer, type IReportDataGetter, type IReportConfig } from './interfaces';
import { ReportData } from './ReportData';
import { ReportConfigSceneScreenshotValue } from './ReportConfigSceneScreenshotValue';

type Constructor<T> = new (...args: Array<any>) => T;

/**
 * Represents a class responsible for gathering report data.
 */
export class ReportDataGatherer implements IReportDataGatherer {
  private _reportData = new ReportData();

  private _reportConfig: IReportConfig;

  private _viewsService: ViewsService;

  private _reportValueAccessorsMap: Map<Constructor<ReportConfigEntryType>, IReportValueAccessor>;

  public constructor(
    config: IReportConfig,
    reportValueAccessorsMap: Map<Constructor<ReportConfigEntryType>, IReportValueAccessor>,
    viewsService: ViewsService
  ) {
    this._reportConfig = config;
    this._reportValueAccessorsMap = reportValueAccessorsMap;
    this._viewsService = viewsService;
  }

  public async gatherReportData(): Promise<IReportDataGetter> {
    this._initDataGatherers();

    const sortedEntries = this._getReportConfigEntriesOptimized();

    return await this._gatherReportDataByEntries(sortedEntries);
  }

  private _initDataGatherers(): void {
    for (const accessor of this._reportValueAccessorsMap.values()) {
      accessor.init?.();
    }
  }

  private async _gatherReportDataByEntries(entries: Array<IConfigProps>): Promise<IReportDataGetter> {
    for (const entry of entries) {
      const accessor = this._reportValueAccessorsMap.get(entry.constructor as Constructor<ReportConfigEntryType>);
      if (!accessor) {
        throw new Error('No accessor for ' + entry.descriptor);
      }
      const value = await accessor.getValueByConfig(entry);
      this._reportData.setReportValue(entry.descriptor, value);
    }

    return this._reportData as IReportDataGetter;
  }

  private _getReportConfigEntriesOptimized(): Array<IConfigProps> {
    const possiblePhases = Array.from(this._reportConfig.entries.values()).map((entry) => entry.phase);
    const uniquePhases = [...new Set(possiblePhases)];
    const viewsDescriptors = this._viewsService.views.map((view) => view.getDescriptor());

    //sort first by view and then by phase
    const sortedEntries = Array.from(this._reportConfig.entries.values()).sort((a, b) => {
      if (a instanceof ReportConfigSceneScreenshotValue && b instanceof ReportConfigSceneScreenshotValue) {
        const aViewIndex = a.viewDescriptor ? viewsDescriptors.indexOf(a.viewDescriptor) : -1;
        const bViewIndex = b.viewDescriptor ? viewsDescriptors.indexOf(b.viewDescriptor) : -1;
        if (aViewIndex < bViewIndex) {
          return -1;
        }
        if (aViewIndex > bViewIndex) {
          return 1;
        }
      }
      return uniquePhases.indexOf(a.phase) - uniquePhases.indexOf(b.phase);
    });

    return sortedEntries;
  }
}
